// Shared brand palette + image-loading helpers for every PDF this app
// generates (contract, receipt voucher, ...) so they stay visually
// consistent and the CloudScreen logo/signature-loading logic lives in
// exactly one place.
const MEDIA_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4000/api').replace(/\/api\/?$/, '');

// A softer, lighter orange than admin-panel's own primaryOrange (#F97316) -
// large filled areas of a very saturated orange feel harsh on paper/PDF.
// BRAND_DARK carries the same hue for text/labels, where a darker shade is
// needed to stay legible on white.
export const BRAND = [251, 146, 60]; // smooth light orange - band/table fills
export const BRAND_DARK = [194, 65, 12]; // darker orange - readable text (labels/markers) on white
export const BRAND_TINT = [255, 237, 213]; // very light orange - highlight/panel backgrounds
export const PANEL_BG = [250, 246, 240]; // faint warm gray-orange - info panel backgrounds
export const TEXT_DARK = [40, 40, 40];

const fetchAsDataUrl = async (absoluteUrl) => {
  try {
    const response = await fetch(absoluteUrl);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

// Resolves an uploaded file's URL (e.g. a signature, "/uploads/...") to a
// data URI jsPDF's addImage can embed. Returns null on any failure (missing
// file, network error, CORS) so a missing image never blocks the rest of
// the PDF from generating.
export const loadUploadedImageAsDataUrl = async (url) => {
  if (!url) return null;
  const absoluteUrl = /^https?:\/\//i.test(url) ? url : `${MEDIA_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
  return await fetchAsDataUrl(absoluteUrl);
};

// The CloudScreen logo is a static asset served by the admin-panel itself
// (admin-panel/public/cloudnet-logo.png) - it lives on THIS app's own
// origin, not the API server, so it must never be resolved against
// MEDIA_BASE_URL the way an uploaded signature is.
export const loadLogoAsDataUrl = () => fetchAsDataUrl('/cloudnet-logo.png');

// Every image jsPDF embeds needs its pixel format known up front - probe it
// off a plain Image element rather than trying to sniff the data URI.
export const getImageDimensions = (dataUrl) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });

// jsPDF's addImage requires the format ('PNG'/'JPEG'/'WEBP') as an explicit
// argument - it does not sniff it from the data URI itself.
export const getImageFormat = (dataUrl) => {
  const match = /^data:image\/(png|jpe?g|webp)/i.exec(dataUrl || '');
  if (!match) return 'PNG';
  const ext = match[1].toLowerCase();
  return ext === 'jpg' ? 'JPEG' : ext.toUpperCase();
};

export const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return `${String(d.getDate()).padStart(2, '0')} / ${String(d.getMonth() + 1).padStart(2, '0')} / ${d.getFullYear()}`;
};

export const formatMoney = (value) =>
  Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
